export enum OrderType {
  ECOMMERCE = "ECOMMERCE",
  INSTORE = "INSTORE",
  MLM = "MLM",
  DIGITAL = "DIGITAL",
  FOOD_ORDER = "FOOD_ORDER",
}

export enum CustomerPurchaseType {
  INSTORE = "INSTORE",
  ONLINE = "ONLINE",
  FOOD = "FOOD",
}

export enum CustomerDeliveryStatus {
  // Generic (E-commerce / Instore)
  PENDING = "PENDING",
  IN_TRANSIT = "IN_TRANSIT",
  OUT_FOR_DELIVERY = "OUT_FOR_DELIVERY",
  DELIVERED = "DELIVERED",
  NOT_DELIVERED = "NOT_DELIVERED",
  // Food order specific
  ORDER_ACCEPTED = "ORDER_ACCEPTED",
  PREPARING = "PREPARING",
  ORDER_READY = "ORDER_READY",
  RIDER_ASSIGNED = "RIDER_ASSIGNED",
  RIDER_ARRIVED_AT_RESTAURANT = "RIDER_ARRIVED_AT_RESTAURANT",
  ORDER_PICKED_UP = "ORDER_PICKED_UP",
  RIDER_ARRIVED_AT_LOCATION = "RIDER_ARRIVED_AT_LOCATION",
  CANCELLED = "CANCELLED",
}

export interface CustomerOrderActions {
  canCancel: boolean;
  canReturn: boolean;
  canReplace: boolean;
  cancellationDeadline?: string;
  returnDeadline?: string;
  replacementDeadline?: string;
  refundStatus?: string;
  refundAmount?: number;
  returnStatus?: string;
  replacementStatus?: string;
}

export interface CustomerCancelOrderDto {
  reason: string;
}

export interface CustomerReturnRequestDto {
  reason: string;
  details?: string;
}

export interface CustomerReplacementRequestDto {
  reason: string;
  details?: string;
}

export enum OrderStatus {
  PENDING = "PENDING",
  ACCEPTED = "ACCEPTED",
  CONFIRMED = "CONFIRMED",
  PROCESSING = "PROCESSING",
  SHIPPED = "SHIPPED",
  DELIVERED = "DELIVERED",
  CANCELLED = "CANCELLED",
  REFUNDED = "REFUNDED",
  RETURN_REQUESTED = "RETURN_REQUESTED",
  RETURN_APPROVED = "RETURN_APPROVED",
  RETURNED = "RETURNED",
  REPLACEMENT_REQUESTED = "REPLACEMENT_REQUESTED",
  REPLACEMENT_APPROVED = "REPLACEMENT_APPROVED",
  REPLACED = "REPLACED",
}

export enum PaymentStatus {
  PENDING = "PENDING",
  PROCESSING = "PROCESSING",
  COMPLETED = "COMPLETED",
  FAILED = "FAILED",
  REFUNDED = "REFUNDED",
  PARTIALLY_REFUNDED = "PARTIALLY_REFUNDED",
  CANCELLED = "CANCELLED",
}

export interface CustomerProductDetailsDto {
  productId:string;
  productImage?: string;
  productName?: string;
  productType?: string;
  validity?: string;
  validFrom?: string;
  validTo?: string;
  receivedBenefits?: string;
  quantity?: number;
  sourceType?: string;
  unitPrice?: number;
  totalPrice?: number;
  sellerId?: string;
  sellerName?: string;
  sku?: string;
}

export interface CustomerOffersAppliedDto {
  salarCode?: string;
  salarCodeDiscount: number;
  salarShoppingVoucher: number;
  salarRuby: number;
  totalDiscount: number;
}

export interface CustomerFinalAmountDto {
  amount: number;
  platformFee: number;
  deliveryCharges: number;
  finalPrice: number;
}

export interface CustomerPaymentDetailsDto {
  salarPayAmount: number;
  onlinePaymentAmount: number;
  onlinePaymentGateway?: string;
  paymentInstrument?: string;
  gatewayTransactionId?: string;
  salarPayBreakdown?: {
    shoppingVoucher: number;
    salarRuby: number;
    salarCode: number;
  };
}

export interface CustomerRefundDetailsDto {
  refundId?: string;
  refundNumber?: string;
  reason?: string;
  refundAmount: number;
  refundStatus:
    | "NOT_APPLICABLE"
    | "PENDING"
    | "PROCESSING"
    | "COMPLETED"
    | "FAILED"
    | "REFUNDED";
  refundInitiatedAt?: string;
  refundCompletedAt?: string;
  estimatedRefundDays?: number;
}

export interface CustomerSellerDetailsDto {
  sellerId?: string;
  sellerName?: string;
  sellerLocation?: string;
}

export interface CustomerDeliveryAddressDto {
  fullName?: string;
  mobile?: string;
  email?: string;
  addressLine1?: string;
  addressLine2?: string;
  landmark?: string;
  city?: string;
  district?: string;
  state?: string;
  pincode?: string;
  country?: string;
}

export interface CustomerShipmentInfoDto {
  trackingNumber?: string;
  trackingUrl?: string;
  courierPartner?: string;
  packingStatus?: string;
  packagesCount?: number;
  deliveryType?: string;
  shippingType?: string;
  deliveryEstimatedDays?: number;
  deliveryOptionDescription?: string;
  acceptedAt?: string;
  confirmedAt?: string;
  shippedAt?: string;
  deliveredAt?: string;
  cancelledAt?: string;
}

export interface CustomerCouponDetailsDto {
  couponId?: string;
  couponCode: string;
  couponType?: string;
  couponDiscount: number;
}

export interface Order {
  sNo: number;
  orderId: string;
  orderDate: string;
  orderNo: string;
  purchaseType: CustomerPurchaseType;
  deliveryStatus: CustomerDeliveryStatus;
  sellerDetails: CustomerSellerDetailsDto;
  productDetails: CustomerProductDetailsDto[];
  payableAmount: number;
  offersApplied: CustomerOffersAppliedDto;
  finalAmount: CustomerFinalAmountDto;
  paymentStatus: string;
  paymentDetails: CustomerPaymentDetailsDto;
  refundDetails: CustomerRefundDetailsDto;
  invoice?: string;
  salarCoinsEarned?: number;
  trackingNumber?: string;
  trackingUrl?: string;
  deliveryAddress?: CustomerDeliveryAddressDto;
  shipmentInfo?: CustomerShipmentInfoDto;
  couponDetails?: CustomerCouponDetailsDto;

  // Backward compatibility fields (optional)
  id?: string;
  currency?: string;
  orderNumber?: string;
  refundStatus?: string;
  deliveryFullName?: string;
  deliveryAddressLine1?: string;
  deliveryAddressLine2?: string;
  deliveryLandmark?: string;
  deliveryCity?: string;
  deliveryState?: string;
  deliveryPincode?: string;
  deliveryCountry?: string;
  deliveryMobile?: string;
}

export interface OrderQuery {
  page?: number;
  limit?: number;
  paymentStatus?: PaymentStatus | string;
  purchaseType?: CustomerPurchaseType | string;
  deliveryStatus?: CustomerDeliveryStatus | string;
  startDate?: string;
  endDate?: string;
  search?: string;
  sortBy?: string;
  sortOrder?: "ASC" | "DESC";
}

export interface OrdersResponse {
  success: boolean;
  data: {
    orders: Order[];
    pagination: {
      page: number;
      limit: number;
      total: number;
      totalPages: number;
    };
  };
}

export interface OrderResponse {
  success: boolean;
  data: Order;
}

export interface OrderActionsResponse {
  success: boolean;
  data: CustomerOrderActions;
}

export interface TrackingHistoryItem {
  status: string;
  location?: string;
  timestamp: string;
  remarks?: string;
}

export interface TrackingData {
  success: boolean;
  deliveryRequestId: string | null;
  trackingNumber?: string;
  awbNumber?: string;
  courierPartner?: string;
  currentStatus: string;
  currentLocation?: string;
  estimatedDeliveryDate?: string;
  deliveredAt?: string;
  trackingUrl?: string;
  trackingHistory: TrackingHistoryItem[];
  message?: string;
  // Extra fields for TrackPackagePage
  deliveryAddress?: CustomerDeliveryAddressDto;
  pricing?: {
    listingPrice: number;
    sellingPrice: number;
    discount: number;
    deliveryFee: number;
    platformFee: number;
    totalPaid: number;
  };
  productSummary?: {
    productName?: string;
    productImage?: string;
    quantity?: number;
    unitPrice?: number;
  };
  orderNumber?: string;
  orderDate?: string;
}

export interface TrackingResponse {
  success: boolean;
  data: TrackingData;
}

export interface OrderState {
  orders: Order[];
  currentOrder: Order | null;
  actions: CustomerOrderActions | null;
  actionsMap: Record<string, CustomerOrderActions>;
  tracking: TrackingResponse["data"] | null;
  loading: boolean;
  csvLoading: boolean;
  error: string | null;
  success: boolean;
  total: number;
  page: number;
  limit: number;
  totalPages: number;
  summary: CustomerOrdersSummaryDto | null;
}

export interface CustomerOrdersSummaryDto {
  totalOrders: number;
  successfulOrders: number;
  pendingOrders: number;
  cancelledOrders: number;
  totalAmountSpent: number;
  totalSalarCoinsEarned: number;
  totalRefundAmount: number;
}

export interface OrdersSummaryResponse {
  success: boolean;
  data: CustomerOrdersSummaryDto;
}
